import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Printer,
  Download,
  FileText,
} from 'lucide-react';
import { useComprobantes, useDeleteComprobante } from '../../hooks/useComprobantes';
import { Button } from '../../components/ui/Button';
import { formatCurrency, formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../api/clients';

export function ComprobanteList() {
  const [searchTerm, setSearchTerm] = useState('');
  const { data: comprobantes, isLoading, isError, refetch } = useComprobantes();
  const deleteComprobante = useDeleteComprobante();

  const filtered = comprobantes?.filter((c: any) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      c.clienteNombre?.toLowerCase().includes(searchLower) ||
      c.clienteDocumento?.toLowerCase().includes(searchLower) ||
      c.clienteDireccion?.toLowerCase().includes(searchLower) ||
      c.numeroComprobante?.toLowerCase().includes(searchLower)
    );
  }) || [];

  const handleDelete = async (id: number, cliente: string) => {
    if (!confirm(`¿Estás seguro de eliminar el comprobante de "${cliente}"?`)) return;

    try {
      await deleteComprobante.mutateAsync(id);
      refetch();
    } catch (error: any) {
      // Error ya manejado por react-query
    }
  };

  const handleGeneratePDF = async (id: number) => {
    try {
      const response = await api.post(`/comprobantes/${id}/pdf`);
      if (response.data.success) {
        toast.success('PDF generado exitosamente');
        refetch();
        window.open(response.data.pdfUrl, '_blank');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al generar PDF');
    }
  };

  const handleDownloadPDF = async (id: number) => {
    try {
      const response = await api.get(`/comprobantes/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comprobante-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al descargar PDF');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando comprobantes...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-8 text-red-500">
        Error al cargar los comprobantes
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Comprobantes</h1>
          <p className="text-sm text-gray-500">
            {filtered.length} comprobante(s) registrados
          </p>
        </div>
        <Link to="/comprobantes/nuevo">
          <Button className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus size={18} />
            Nuevo Comprobante
          </Button>
        </Link>
      </div>

      {/* Búsqueda */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Buscar por cliente, documento o dirección..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>

      {/* Lista de comprobantes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((comprobante: any) => {
          const productos = typeof comprobante.productosJson === 'string'
            ? JSON.parse(comprobante.productosJson)
            : comprobante.productosJson || [];
          
          const totalProductos = productos.length;
          const totalUnidades = productos.reduce((sum: number, p: any) => sum + (p.cantidad || 0), 0);

          return (
            <div key={comprobante.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-800 truncate max-w-45">
                    {comprobante.clienteNombre}
                  </h3>
                  <p className="text-xs text-gray-400">Doc: {comprobante.clienteDocumento || 'N/A'}</p>
                </div>
                <div className="flex gap-1">
                  <Link
                    to={`/comprobantes/${comprobante.id}`}
                    className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                  >
                    <Eye size={16} />
                  </Link>
                  <Link
                    to={`/comprobantes/editar/${comprobante.id}`}
                    className="p-1.5 text-gray-500 hover:bg-gray-50 rounded transition-colors"
                  >
                    <Edit size={16} />
                  </Link>
                  <button
                    onClick={() => handleDelete(comprobante.id, comprobante.clienteNombre)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Detalles */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Productos</span>
                  <span className="font-medium text-gray-700">
                    {totalProductos} ({totalUnidades} und.)
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total</span>
                  <span className="font-medium text-gray-700">
                    {formatCurrency(comprobante.total || 0)}
                  </span>
                </div>
                {comprobante.costoEnvio > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Envío</span>
                    <span className="text-xs text-gray-600">
                      {formatCurrency(comprobante.costoEnvio)}
                    </span>
                  </div>
                )}
                {comprobante.promocionPorcentaje > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Promoción</span>
                    <span className="text-xs text-green-600">
                      -{comprobante.promocionPorcentaje}%
                    </span>
                  </div>
                )}
                {comprobante.clienteDireccion && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Dirección</span>
                    <span className="text-xs text-gray-600 truncate max-w-37.5">
                      {comprobante.clienteDireccion}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Fecha</span>
                  <span className="text-xs text-gray-400">
                    {formatDate(comprobante.fechaEmision)}
                  </span>
                </div>
              </div>

              {/* Acciones */}
              <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2">
                {comprobante.pdfUrl ? (
                  <button
                    onClick={() => handleDownloadPDF(comprobante.id)}
                    className="flex-1 flex items-center justify-center gap-1 text-sm text-gray-700 border border-gray-300 hover:bg-gray-50 py-1.5 rounded-lg transition-colors"
                  >
                    <Download size={14} />
                    Descargar
                  </button>
                ) : (
                  <button
                    onClick={() => handleGeneratePDF(comprobante.id)}
                    className="flex-1 flex items-center justify-center gap-1 text-sm text-gray-700 border border-gray-300 hover:bg-gray-50 py-1.5 rounded-lg transition-colors"
                  >
                    <Printer size={14} />
                    Generar PDF
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <FileText size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No se encontraron comprobantes</p>
          {searchTerm && (
            <p className="text-sm mt-1">
              No hay comprobantes que coincidan con "{searchTerm}"
            </p>
          )}
          <Link to="/comprobantes/nuevo" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
            Crear el primer comprobante
          </Link>
        </div>
      )}
    </div>
  );
}